import { useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useSelector } from "src/store";
import { SwitchField } from "src/ui/shared/form/SwitchField";
import { makeStyles } from "tss-react/mui";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { SectionDescription } from "@dashboard/shared/styles";
import { Box } from "@mui/material";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";
import { FormValues as ProvisioningFormValues } from "../ProvisioningPage";

const useStyles = makeStyles()((theme) => ({
    columnContainer: {
        paddingTop: theme.spacing(8),
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(4),
        display: "flex",
        justifyContent: "space-between",
    },
    column: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
    },
}));

export const Connectivity = () => {
    const { value, onChange } = useCollapsiblePersistence("provisioningPage.collapsedSections", "Connectivity");
    const { classes: styles } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const { values: parentFormValues } = useFormState<Partial<ProvisioningFormValues>>();
    const mdmClientEnabled = parentFormValues?.settings?.enableMdmClient;

    const hasGroupSelected = Boolean(
        parentFormValues?.settings?.universalLicenses.length || parentFormValues?.registrationGroupId,
    );
    const isWifiDisabled = !parentFormValues?.settings?.internetConfig?.find((item) => item.type === "wifi");

    return (
        <CollapsibleProvider initialValue={value} onChange={onChange}>
            <CollapsibleSectionSummary>
                <CollapsibleSectionTitle>{t("pages:provisioningPage.section.connectivity.title")}</CollapsibleSectionTitle>
            </CollapsibleSectionSummary>
            <CollapsibleSectionDetails>
                <SectionDescription>{t("pages:provisioningPage.section.connectivity.description")}</SectionDescription>
                <Box className={styles.columnContainer}>
                    <div className={styles.column}>
                        <SwitchField
                            label={t("pages:provisioningPage.input.enableSshServer.caption")}
                            name="settings.enableSshServer"
                            labelPlacement="end"
                            disabled={!provisionings.length}
                        />
                        <SwitchField
                            label={t("pages:provisioningPage.input.enableMdmClient.caption")}
                            name="settings.enableMdmClient"
                            labelPlacement="end"
                            disabled={!hasGroupSelected && !mdmClientEnabled}
                            errorText={
                                !hasGroupSelected
                                    ? t("pages:provisioningPage.input.enableMdmClient.validation.required")
                                    : undefined
                            }
                        />
                        <SwitchField
                            label={t("pages:provisioningPage.input.enableSftpServer.caption")}
                            name="settings.enableSftpServer"
                            labelPlacement="end"
                            disabled={!provisionings.length}
                        />
                    </div>

                    <div className={styles.column}>
                        <SwitchField
                            label={t("pages:provisioningPage.input.enableBluetooth.caption")}
                            name="settings.enableBluetooth"
                            labelPlacement="end"
                            disabled={!provisionings.length}
                        />
                        <SwitchField
                            label={t("pages:provisioningPage.input.enableWifi.caption")}
                            name="settings.enableWifi"
                            labelPlacement="end"
                            disabled={isWifiDisabled}
                            errorText={
                                isWifiDisabled ? t("pages:provisioningPage.input.enableWifi.validation.required") : undefined
                            }
                        />
                    </div>

                    <div className={styles.column}>
                        <SwitchField
                            label={t("pages:provisioningPage.input.enableAdb.caption")}
                            name="settings.enableAdbServer"
                            labelPlacement="end"
                            disabled={!provisionings.length}
                        />
                        <SwitchField
                            label={t("pages:provisioningPage.input.enableVnc.caption")}
                            name="settings.enableVncServer"
                            labelPlacement="end"
                            disabled={!provisionings.length}
                        />
                    </div>
                </Box>
            </CollapsibleSectionDetails>
        </CollapsibleProvider>
    );
};
